  import React from 'react';
  import FaqAnswersComponent from './FaqAnswersComponent';
  import OptionsDisplay from './OptionsDisplay';
  import OffersCard from './OffersCard';
  import BillSummary from './BillSummary';
  import OptionsSummaryCard from './OptionsSummaryCard';
  import UsageCard from './UsageCard';
  import Carousel from './Carousel';
  import OffersComparisonCard from './OffersComparisonCard';
  import CurrentPlanCard from './CurrentPlanCard';
  import SelectedPlanCard from './SelectedPlanCard';
  import OrderArrivalCard from './orderArrivalCard';
  import QRCodeComponent from './QRCodeComponent';
  import WelcomeMessageComponent from './WelcomeMessageComponent';

  // Component Registry - Maps componentType to React component
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export const ComponentRegistry: Record<string, React.ComponentType<any>> = {
    qrcode: QRCodeComponent,
    qrCode: QRCodeComponent, // Support camelCase version
    QRCode: QRCodeComponent, // Support PascalCase version
    faqAnswersComponent: FaqAnswersComponent,
    optionsDisplay: OptionsDisplay,
    offersCard: OffersCard,
    billSummary: BillSummary,
    optionsSummaryCard: OptionsSummaryCard,
    usageCard: UsageCard,
    carousel: Carousel,
    offersComparisonCard: OffersComparisonCard,
    currentPlanCard: CurrentPlanCard,
    currentplancard: CurrentPlanCard, // Support lowercase version from API
    selectedPlanCard: SelectedPlanCard,
    orderArrivalCard: OrderArrivalCard,
    welcomeMessage: WelcomeMessageComponent,
    // Add new components here as they are created
    // proRataCard: ProRataCard,
  };

  // Helper function to get props for a specific component type
  export const getComponentProps = (
    componentType: string, 
    content: unknown, 
    handlers?: {
      onOptionSelect?: (option: string) => void;
      onCtaClick?: (offer: unknown) => void;
      onSendMessage?: (content: string) => void;
    },
    isCarousel?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any => {
    switch (componentType) {
      case 'qrcode': {
        const qrContent = content as { header?: string; message?: string; imageLink?: string } | Array<{ header?: string; message?: string; imageLink?: string }>;
        
        // Handle both single object and array formats
        const contentArray = Array.isArray(qrContent) ? qrContent : [qrContent];
        
        return {
          content: contentArray.map(item => ({
            header: item?.header || 'Continue via our app anytime',
            imageLink: item?.imageLink || 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=app-download'
          })),
          isCarousel
        };
      }
      case 'faqAnswersComponent': {
        const faqContent = content as { title?: string; message?: string };
        return {
          title: faqContent?.title || 'FAQ',
          message: faqContent?.message || ''
        };
      }
      case 'optionsDisplay': {
        const optionsContent = content as { 
          options?: string[];
          layout?: 'vertical' | 'horizontal';
        };
        return {
          options: Array.isArray(optionsContent?.options) ? optionsContent.options : [],
          layout: optionsContent?.layout || 'horizontal', // Default to horizontal
          onOptionSelect: handlers?.onOptionSelect
        };
      }
      case 'offersCard': {
        // Handle both array format and single object format
        if (Array.isArray(content)) {
          // If content is an array, pass it directly to the component
          return {
            content: content,
            onCtaClick: handlers?.onCtaClick,
            onSendMessage: handlers?.onSendMessage,
            isCarousel: isCarousel
          };
        }
        
        // Handle single object format (transformed props)
        const offersContent = content as {
          header?: string;
          title?: string;
          description?: string;
          options?: Array<{ key: string; value: string }>;
          discountAmount?: string;
          actualAmount?: string;
          currency?: string;
          cta?: string;
        };
        
        // Extract currency symbol and amounts properly
        const actualAmount = offersContent?.actualAmount || '€0';
        const discountAmount = offersContent?.discountAmount || '€0';
        const currencySymbol = actualAmount.match(/[€$£¥]/)?.[0] || '€';
        
        // Parse numbers for calculation
        const actualNumeric = parseInt(actualAmount.replace(/[^0-9]/g, '')) || 0;
        const discountNumeric = parseInt(discountAmount.replace(/[^0-9]/g, '')) || 0;
        const originalPrice = actualNumeric + discountNumeric;
        
        return {
          header: offersContent?.header || 'OFFER',
          title: offersContent?.title || 'Special Offer',
          description: offersContent?.description || '',
          price: `${actualAmount}/m`,
          originalPrice: originalPrice > actualNumeric ? `${currencySymbol}${originalPrice}/m` : '',
          discount: discountNumeric > 0 ? discountAmount : '',
          ctaText: offersContent?.cta || 'Select Plan',
          features: Array.isArray(offersContent?.options) 
            ? offersContent.options.map(option => `${option.key}: ${option.value}`)
            : [],
          onCtaClick: handlers?.onCtaClick,
          onSendMessage: handlers?.onSendMessage,
          isCarousel: isCarousel
        };
      }
      case 'billSummary':
        return {
          content: content
        };
      case 'optionsSummaryCard': {
        // Handle both array format and single object format
        let summaryContent: { 
          title?: string; 
          description?: string;
          header?: string;
          options?: Array<{ text: string; checked?: boolean }> | string[];
        };
        
        if (Array.isArray(content) && content.length > 0) {
          // Extract from array format: content[0]
          summaryContent = content[0] as { 
            title?: string; 
            description?: string;
            header?: string;
            options?: string[];
          };
        } else {
          // Single object format
          summaryContent = content as { 
            title?: string; 
            description?: string;
            header?: string;
            options?: Array<{ text: string; checked?: boolean }> | string[];
          };
        }
        
        console.log('optionsSummaryCard - Raw content received:', content);
        console.log('optionsSummaryCard - Processed summaryContent:', summaryContent);
        
        // Handle both object format and string array format for options
        let processedOptions: Array<{ text: string; checked?: boolean }> = [];
        
        if (Array.isArray(summaryContent?.options)) {
          processedOptions = summaryContent.options.map(option => {
            if (typeof option === 'string') {
              // Convert string to object format
              return { text: option, checked: false };
            } else {
              // Already in object format
              return option;
            }
          });
        }
        
        const result = {
          header: summaryContent?.header || 'OPTIONS',
          title: summaryContent?.title || 'Options Summary',
          description: summaryContent?.description,
          options: processedOptions,
          isCarousel: isCarousel
        };
        
        console.log('optionsSummaryCard - Final result being returned:', result);
        
        return result;
      }
      case 'usageCard': {
        const usageContent = content as { title?: string; metrics?: Array<{ icon: string; value: string; description: string }> };
        return {
          title: usageContent?.title || 'Usage Statistics',
          metrics: Array.isArray(usageContent?.metrics) ? usageContent.metrics : [],
          isCarousel: isCarousel
        };
      }
      case 'carousel': {
        const carouselContent = content as { 
          items?: Array<{ componentType: string; content: unknown }>;
          viewMode?: 'widget' | 'fullscreen';
        };
        
        // Auto-detect view mode if not specified
        let viewMode = carouselContent?.viewMode || 'widget';
        
        // Try to detect if we're in a widget or fullscreen context
        // Check if we're inside a chatbot widget (small container)
        if (typeof window !== 'undefined') {
          const chatContainer = document.querySelector('.chatbot');
          if (chatContainer) {
            const containerWidth = chatContainer.clientWidth;
            // If container is less than 600px, assume widget mode
            if (containerWidth < 600) {
              viewMode = 'widget';
            } else {
              viewMode = 'fullscreen';
            }
          }
        }
        
        // Generate unique carousel ID based on content and timestamp
        const carouselId = `carousel-${Date.now()}-${JSON.stringify(carouselContent?.items).slice(0, 50).replace(/[^a-zA-Z0-9]/g, '')}-${Math.random().toString(36).substr(2, 5)}`;
        
        return {
          items: Array.isArray(carouselContent?.items) ? carouselContent.items : [],
          handlers: handlers,
          viewMode: viewMode,
          carouselId: carouselId
        };
      }
      case 'offersComparisonCard': {
        const comparisonContent = content as {
          header?: string;
          title?: string;
          description?: string;
          options?: Array<{ bundleName: string; amount: string; currency: string }>;
          cta?: string;
        };
        
        return {
          title: comparisonContent?.header || comparisonContent?.title || 'Plan Comparison',
          description: comparisonContent?.description || '',
          offers: Array.isArray(comparisonContent?.options) 
            ? comparisonContent.options.map(option => ({
                name: option.bundleName || 'Plan',
                price: `${option.amount || '0'}`,
                features: [] // Not provided in backend response
              }))
            : [],
          ctaText: comparisonContent?.cta || 'Compare',
          onCtaClick: handlers?.onCtaClick,
          isCarousel: isCarousel
        };
      }
      case 'currentPlanCard':
      case 'currentplancard': {
        // Backend sends: content: [{ header, title, description, options: [{key, value}], actualAmount, currency }]
        // or content: { header, title, description, options: [{key, value}], actualAmount, currency }
        console.log('currentPlanCard - Raw content received:', content);
        console.log('currentPlanCard - Content type:', typeof content);
        
        // Pass content directly to component - no transformation needed
        const result = {
          content: content,
          isCarousel: isCarousel
        };
        
        console.log('currentPlanCard - Final result being returned:', result);
        
        return result;
      }
      case 'selectedPlanCard': {
        // Pass content directly to component - no transformation needed
        return {
          content: content,
          isCarousel: isCarousel
        };
      }
      case 'orderArrivalCard': {
        // Pass content directly to component - no transformation needed
        return {
          content: content,
          isCarousel: isCarousel
        };
      }
      case 'welcomeMessage': {
        // Pass onSendMessage handler to welcome message component
        return {
          content: content,
          onSendMessage: handlers?.onSendMessage,
          isCarousel: isCarousel
        };
      }
      default:
        return content;
    }
  };

  // Export individual components
  export {default as QRCode} from './QRCodeComponent';
  export { default as FaqAnswersComponent } from './FaqAnswersComponent';
  export { default as OptionsDisplay } from './OptionsDisplay';
  export { default as OffersCard } from './OffersCard';
  export { default as BillSummary } from './BillSummary';
  export { default as OptionsSummaryCard } from './OptionsSummaryCard';
  export { default as UsageCard } from './UsageCard';
  export { default as Carousel } from './Carousel';
  export { default as OffersComparisonCard } from './OffersComparisonCard';
  export { default as CurrentPlanCard } from './CurrentPlanCard';
  export { default as SelectedPlanCard } from './SelectedPlanCard';
  export { default as OrderArrivalCard } from './orderArrivalCard';
  export { default as WelcomeMessageComponent } from './WelcomeMessageComponent';
